import React from "react";
import { Link } from "react-router-dom";
import InnerLayout from "../InnerLayout";
import { QuickFactsCard } from "./CampusLifePageParts";
import {
  ArrowLeft,
  Wrench,
  Factory,
  Building2,
  Award,
  CheckCircle2,
} from "lucide-react";

const welding = {
  name: "Welding Research Center",

  description:
    "The Welding Research Center at KLS Vishwanathrao Deshpande Institute of Technology (KLS VDIT), Haliyal, has been established under the Department of Mechanical Engineering with financial assistance from the Karnataka Council for Technological Upgradation (KCTU), Government of Karnataka. The centre promotes advanced research, innovation, skill development, and industrial collaboration in welding technologies and modern manufacturing processes.",

  overview: [
    "The Karnataka Council for Technological Upgradation (KCTU) is a joint initiative of the Government of India and the Government of Karnataka established to promote technological advancement, innovation, and industrial growth. As a premier council implementing schemes under the Karnataka Industrial Policy, KCTU supports the establishment and strengthening of Research & Development centres across institutions.",
    "The council provides financial assistance for developing advanced research infrastructure, modern equipment, and technological facilities to encourage innovation, product development, and sustainable growth in the manufacturing sector.",
    "Recognizing the research potential of KLS VDIT, the Seventh Level Committee of KCTU sanctioned financial assistance for establishing the Welding Research Center in the Department of Mechanical Engineering.",
    "The centre facilitates advanced research, skill development, and innovation in welding technologies, manufacturing processes, and industrial applications while contributing to the growth of engineering education and the manufacturing industry.",
  ],

  dashboard: [
    ["Government Funding", "₹22.78 Lakhs"],
    ["Institution Funding", "₹22.78 Lakhs"],
    ["Project Cost", "₹45.55 Lakhs"],
    ["Research Centre", "01"],
  ],

  projectDetails: [
    ["Project Title", "Welding Research Center"],
    [
      "Supporting Organization",
      "Karnataka Council for Technological Upgradation (KCTU), Department of Industries & Commerce, Government of Karnataka",
    ],
    [
      "Purpose",
      "Financial Assistance to establish a Welding Research Center",
    ],
    ["Department", "Mechanical Engineering"],
    ["Government Contribution", "₹22.78 Lakhs"],
    ["Institution Contribution", "₹22.78 Lakhs"],
    ["Total Project Cost", "₹45.55 Lakhs"],
    ["Principal Investigator", "Prof. K. S. Pujar"],
  ],

  objectives: [
    "Promote advanced research in welding technologies.",
    "Encourage innovation in manufacturing processes.",
    "Support product development and industrial applications.",
    "Strengthen industry-institute collaboration.",
    "Develop skilled manpower in welding engineering.",
    "Provide modern research infrastructure for students and faculty.",
    "Encourage sustainable manufacturing practices.",
  ],

  researchAreas: [
    "Advanced Welding Technology",
    "Manufacturing Processes",
    "Industrial Welding Applications",
    "Product Development",
    "Sustainable Manufacturing",
    "Materials Engineering",
  ],

  facilities: [
    "Modern Welding Research Laboratory",
    "Advanced Welding Equipment",
    "Manufacturing Research Facilities",
    "Industrial Testing Support",
    "Skill Development Infrastructure",
  ],

  benefits: [
    "Hands-on research opportunities for students.",
    "Industry-oriented project development.",
    "Innovation and product development support.",
    "Faculty research enhancement.",
    "Industrial consultancy opportunities.",
    "Support for higher education and doctoral research.",
  ],
};

const quickFacts = [
  ["Centre", "Welding Research Center"],
  ["Department", "Mechanical Engineering"],
  ["Funding Agency", "KCTU"],
  ["Government Support", "₹22.78 Lakhs"],
  ["Institution Support", "₹22.78 Lakhs"],
  ["Total Project Cost", "₹45.55 Lakhs"],
  ["Principal Investigator", "Prof. K. S. Pujar"],
  ["Government", "Government of Karnataka"],
];

const WeldingResearchCenter = () => {
  return (
    <InnerLayout
      title={welding.name}
      image="https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?w=2400&q=80"
      breadcrumbs={[
        { label: "Campus Life", to: "/campus" },
        { label: "Welding Research Center" },
      ]}
      sidebar={null}
    >
      <div className="grid lg:grid-cols-3 gap-10 items-start">
        <section className="lg:col-span-2">
          <Link
            to="/campus"
            className="inline-flex items-center gap-2 text-sm text-brand font-semibold hover:underline mb-6"
          >
            <ArrowLeft size={16} />
            Back to Campus Life
          </Link>

          <h2
            className="text-3xl md:text-4xl text-brand font-semibold mb-5"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            About the Centre
          </h2>

          <p className="text-[#2a2a2a]/85 leading-relaxed text-[16px]">
            {welding.description}
          </p>

          <div className="space-y-4 mt-6">
            {welding.overview.map((paragraph) => (
              <p
                key={paragraph}
                className="text-[#2a2a2a]/85 leading-relaxed text-[16px]"
              >
                {paragraph}
              </p>
            ))}
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-8">
            {welding.dashboard.map(([label, value]) => (
              <div
                key={label}
                className="bg-white border border-brand/15 p-4"
              >
                <p className="text-xs uppercase tracking-widest text-brand font-semibold">
                  {label}
                </p>
                <p className="text-2xl text-brand font-semibold mt-2">
                  {value}
                </p>
              </div>
            ))}
          </div>

          {/* Project Details */}

          <div className="mt-12">
            <h2
              className="text-3xl text-brand font-semibold mb-5 flex items-center gap-2"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              <Building2 size={24} />
              Project Details
            </h2>

            <div className="overflow-x-auto bg-white border border-brand/15">
              <table className="w-full border-collapse">
                <tbody>
                  {welding.projectDetails.map(([label, value]) => (
                    <tr key={label}>
                      <td className="border border-brand/15 px-4 py-3 font-semibold w-64 bg-surface-alt/40">
                        {label}
                      </td>
                      <td className="border border-brand/15 px-4 py-3">
                        {value}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Objectives */}

          <div className="mt-12">
            <h2
              className="text-3xl text-brand font-semibold mb-5 flex items-center gap-2"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              <Award size={24} />
              Objectives
            </h2>

            <div className="grid md:grid-cols-2 gap-4">
              {welding.objectives.map((item) => (
                <div
                  key={item}
                  className="bg-white border border-brand/15 p-5 flex gap-3"
                >
                  <CheckCircle2
                    size={18}
                    className="text-brand flex-shrink-0 mt-1"
                  />
                  <p className="text-[#2a2a2a]/85">{item}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Research Areas */}

          <div className="mt-12">
            <h2
              className="text-3xl text-brand font-semibold mb-5 flex items-center gap-2"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              <Factory size={24} />
              Research Areas
            </h2>

            <div className="grid md:grid-cols-2 gap-4">
              {welding.researchAreas.map((area) => (
                <div
                  key={area}
                  className="bg-white border border-brand/15 p-5"
                >
                  <p className="font-semibold text-brand">{area}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Facilities & Benefits */}

          <div className="grid lg:grid-cols-2 gap-8 mt-12">
            <div className="bg-white border border-brand/15 p-5">
              <h3 className="text-2xl text-brand font-semibold flex items-center gap-2">
                <Wrench size={20} />
                Facilities
              </h3>

              <ul className="list-disc pl-5 mt-4 space-y-2 text-[#2a2a2a]/85">
                {welding.facilities.map((facility) => (
                  <li key={facility}>{facility}</li>
                ))}
              </ul>
            </div>

            <div className="bg-white border border-brand/15 p-5">
              <h3 className="text-2xl text-brand font-semibold">
                Benefits
              </h3>

              <ul className="list-disc pl-5 mt-4 space-y-2 text-[#2a2a2a]/85">
                {welding.benefits.map((benefit) => (
                  <li key={benefit}>{benefit}</li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <aside className="bg-white border border-brand/15 p-5 lg:sticky lg:top-36">
          <QuickFactsCard facts={quickFacts} />
        </aside>
      </div>
    </InnerLayout>
  );
};

export default WeldingResearchCenter;
